import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { DataTable, type Column } from '../components/ui/data-table.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { Input } from '../components/ui/input.js';

interface Webhook {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  description?: string;
  created_at: string;
}

interface ListResponse {
  data: Webhook[];
}

export function WebhooksPage(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => api.get('/v1/webhooks') as Promise<ListResponse>,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    if (!s) return data.data;
    return data.data.filter((row) =>
      [row.url, row.description, ...row.events].some((v) => v && v.toLowerCase().includes(s)),
    );
  }, [data, search]);

  const deleteOne = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/webhooks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  const columns: Column<Webhook>[] = [
    {
      key: 'url',
      header: 'URL',
      render: (row) => (
        <Link
          to={`/webhooks/${row.id}`}
          className="font-mono text-xs hover:text-accent block truncate max-w-md"
          title={row.url}
        >
          {row.url}
        </Link>
      ),
    },
    {
      key: 'events',
      header: 'Events',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.events.map((e) => (
            <Badge key={e} tone="neutral">
              {e}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (row) => row.description ?? <span className="text-ink-muted">—</span>,
    },
    {
      key: 'created',
      header: 'Created',
      render: (row) => new Date(row.created_at).toLocaleDateString(),
      width: '120px',
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      className: 'text-right',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (window.confirm(`Delete webhook for ${row.url}?`)) deleteOne.mutate(row.id);
          }}
          disabled={deleteOne.isPending}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Webhooks</h1>
          <p className="text-sm text-ink-muted">
            HTTPS endpoints we POST to when domain events fire. HMAC-signed; the secret is
            shown once at creation. Webhooks are immutable — to change URL or events, delete
            and recreate.
          </p>
        </div>
        <Link to="/webhooks/new">
          <Button>New webhook</Button>
        </Link>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All webhooks</CardTitle>
          {data && (
            <span className="text-xs text-ink-muted">
              {search ? `${filtered.length} of ${data.data.length}` : data.data.length}
            </span>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {data && (
            <div className="flex gap-2 px-4 py-3 border-b border-border">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search URL, description, event type…"
                className="flex-1"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-sm text-ink-muted hover:text-ink px-2"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {isLoading && <p className="p-4 text-sm text-ink-muted">Loading…</p>}
          {error && <ErrorBanner error={error} />}
          {data && (
            <DataTable
              data={filtered}
              columns={columns}
              rowKey={(r) => r.id}
              emptyState={
                search ? (
                  <EmptyState
                    title="No matches"
                    description={`Nothing matches "${search}". Try a different term or clear the search.`}
                  />
                ) : (
                  <EmptyState
                    title="No webhooks yet"
                    description="Create one to have us POST signed event payloads to your HTTPS endpoint."
                  />
                )
              }
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="m-4 rounded border border-danger bg-surface p-3 text-sm">
      <p className="font-medium text-danger">Failed to load</p>
      <p className="mt-1 text-ink-muted">
        {status ? `HTTP ${status}` : 'Network error'} — {(error as Error).message}
      </p>
    </div>
  );
}
